import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, DatePicker, Form, Select } from 'antd';
// import { useSelector } from 'react-redux';
import { LeftOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlusCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, debounce } from 'underscore';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import 'moment/locale/lt';
import locale from 'antd/es/date-picker/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { Category, GetIndicatorsPayload } from '../../core/indicator/types';
import Main from '../../components/layout/Main';
import { getIndicatorCategories, createIndicatorCategory, updateIndicatorCategory, deleteIndicatorCategory } from '../../core/indicator/indicatorApi';
import { loadSingleIndicatorCategory, resetSingleCategory } from '../../core/indicator/indicatorSlice';

import './indicatorCategoriesListing.scss';

const IndicatorCategoriesListing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const didMountRef = useRef(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [filterParentId, setFilterParentId] = useState<number | null>(null);
    const [form] = Form.useForm();

    const {
        categories,
        isFetching,
        singleCategory,
    } = useSelector((state:CombinedStates) => state.indicator);

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                if (singleCategory?.id) {
                    dispatch(updateIndicatorCategory({
                        id: singleCategory.id,
                        ...values,
                    }));
                } else {
                    dispatch(createIndicatorCategory(values));
                }
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleCategory());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleIndicatorCategory(id));
        setIsModalVisible(true);
    };

    useEffect(() => {
        if (singleCategory && isModalVisible) {
            form.setFieldsValue(singleCategory);
        }
    }, [singleCategory, isModalVisible]);

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleCategory());
    };

    const handleFilterApply = (parentId: number | null) => {
        setFilterParentId(parentId);
        setIsFilterModalVisible(false);
        const payload: GetIndicatorsPayload = {};
        if (searchTerm.trim()) payload.search = searchTerm;
        if (parentId) payload.parent_id = parentId;
        dispatch(getIndicatorCategories(payload));
    };

    const handleFilterReset = () => {
        setFilterParentId(null);
        setIsFilterModalVisible(false);
        const payload: GetIndicatorsPayload = {};
        if (searchTerm.trim()) payload.search = searchTerm;
        dispatch(getIndicatorCategories(payload));
    };

    const debouncedSearch = useCallback(
        debounce((value: string, parentId: number | null) => {
            if (value.trim() !== '') {
                const payload: GetIndicatorsPayload = { search: value };
                if (parentId) payload.parent_id = parentId;
                dispatch(getIndicatorCategories(payload));
            }
        }, 1000),
        [],
    );

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(categories)) {
            dispatch(getIndicatorCategories({}));
        }
    }, []);

    const doSearch = (term: string) => {
        setSearchTerm(term);

        const trimmed = term.trim();

        if (trimmed === '') {
            const payload: GetIndicatorsPayload = {};
            if (filterParentId) payload.parent_id = filterParentId;
            dispatch(getIndicatorCategories(payload));
        } else {
            debouncedSearch(term, filterParentId);
        }
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];
    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];

    const backLink = (
        <Button onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    const handleDelete = (category: Category) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti šią kategoriją? Prieš ištrinant įsitikinkite, kad nėra jokių rodiklių, priskirtų šiai kategorijai, kitu atveju ištrynimas nebus galimas.',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                // Čia iškviesk dispatch su delete thunk
                dispatch(deleteIndicatorCategory(category));
            },
        });
        // Show confirmation dialog and dispatch delete action
    };

    return (
        <>
            <Main
                className="data-listing"
                title={<div>Kategorijų administravimas</div>}
                headerTitle={<div>Kategorijų administravimas</div>}
                menuWidth={350}
                menuPlacement="left"
                toolbar={toolbar}
                showFooter={false}
                floatingHeader={false}
                footerContent={<div />}
                floatingFooter
                backLink={false}
                breadcrumb={null}>
                <Card style={{ marginTop: '20px' }}>
                    <Row className="listing-table-title" justify="space-between" align="middle">
                        <Col>
                            <h3 style={{ marginBottom: 0 }}>Rodiklių kategorijos</h3>
                        </Col>
                        <Col>
                            <Space>
                                <Input
                                    placeholder="Paieška..."
                                    value={searchTerm}
                                    onChange={(e) => doSearch(e.target.value)} />
                                <Tooltip title="Filtruoti pagal tėvinę kategoriją">
                                    <Button
                                        icon={<FilterOutlined />}
                                        type={filterParentId ? 'primary' : 'default'}
                                        onClick={() => setIsFilterModalVisible(true)} />
                                </Tooltip>
                                <Tooltip title="Atnaujinti sąrašą">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => { dispatch(getIndicatorCategories({})); setSearchTerm(''); }} />
                                </Tooltip>
                                <Button loading={isFetching} icon={<PlusCircleOutlined />} type="primary" onClick={() => { dispatch(resetSingleCategory()); setIsModalVisible(true); }}>
                                    Pridėti naują kategoriją
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={categories}
                        loading={isFetching}
                        rowKey="id"
                        columns={[
                            {
                                title: 'Pavadinimas',
                                dataIndex: 'title',
                                key: 'title',
                            },
                            {
                                title: 'Tėvinė kategorija',
                                dataIndex: 'parent_id',
                                key: 'parent_id',
                                render: (_text: number | null, record: Category) => {
                                    if (!record.parent_id) return '—';
                                    return record.parent?.title || record.parent_id;
                                },
                            },
                            {
                                title: 'Sukurta',
                                dataIndex: 'created_at',
                                key: 'created_at',
                                render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Redaguota',
                                dataIndex: 'updated_at',
                                key: 'updated_at',
                                render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Veiksmai',
                                key: 'actions',
                                render: (_text, record) => (
                                    <Space>
                                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>Redaguoti</Button>
                                        <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)}>Pašalinti</Button>
                                    </Space>
                                ),
                            },
                        ]} />
                </Card>
            </Main>
            <Modal
                title={singleCategory?.id ? 'Atnaujinti kategoriją' : 'Sukurti naują kategoriją'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={singleCategory?.id ? 'Atnaujinti' : 'Sukurti'}
                cancelText="Atšaukti">
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[{ required: true, message: 'Įveskite pavadinimą' }]}>
                        <Input placeholder="Kategorijos pavadinimas" />
                    </Form.Item>

                    <Form.Item
                        label="Tėvinė kategorija"
                        name="parent_id">
                        <Select
                            showSearch
                            allowClear
                            placeholder="Pasirinkite tėvinę kategoriją"
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            options={categories
                                .filter((cat: Category) => cat.id !== singleCategory?.id)
                                .map((cat: Category) => ({ label: cat.title, value: cat.id }))} />
                    </Form.Item>

                    <Form.Item
                        label="Aprašymas"
                        name="description">
                        <Input.TextArea placeholder="Kategorijos aprašymas" rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Filtruoti pagal tėvinę kategoriją"
                visible={isFilterModalVisible}
                onCancel={() => setIsFilterModalVisible(false)}
                footer={null}>
                <Select
                    style={{ width: '100%', marginBottom: 16 }}
                    allowClear
                    placeholder="Pasirinkite tėvinę kategoriją"
                    value={filterParentId}
                    onChange={(value) => setFilterParentId(value || null)}
                    options={categories.map((cat: Category) => ({ label: cat.title, value: cat.id }))} />
                <Space>
                    <Button type="primary" onClick={() => handleFilterApply(filterParentId)}>
                        Filtruoti
                    </Button>
                    <Button onClick={handleFilterReset}>
                        Išvalyti filtrą
                    </Button>
                </Space>
            </Modal>
        </>
    );
};

export default IndicatorCategoriesListing;

IndicatorCategoriesListing.propTypes = {};
